
import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { KitchenPhotosSection } from "@/components/seller/profile/KitchenPhotosSection";
import { supabase } from "@/integrations/supabase/client";
import { Checkbox } from "@/components/ui/checkbox";
import { Shimmer } from "@/components/ui/shimmer";

export default function BusinessProfile() {
  const [activeTab, setActiveTab] = useState<string>("profile");
  
  return (
    <div className="container p-4 mx-auto">
      <h1 className="text-2xl font-semibold mb-6">Business Settings</h1>
      
      <Tabs defaultValue="profile" onValueChange={setActiveTab}>
        <TabsList className="w-full mb-8">
          <TabsTrigger value="profile" className="flex-1">Profile</TabsTrigger>
          <TabsTrigger value="kitchen" className="flex-1">Kitchen Photos</TabsTrigger>
        </TabsList>
        
        <TabsContent value="profile">
          <ProfileSettings />
        </TabsContent>
        
        <TabsContent value="kitchen">
          <KitchenPhotosSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProfileSettings() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    businessName: "",
    businessDescription: "",
    taxId: "",
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    state: "",
    postalCode: "",
    cookingSpecialties: "",
    instagramHandle: "",
    tiktokHandle: "",
    facebookHandle: "",
    pinterestHandle: "",
    yearsExperience: "",
    otherExperience: "",
    prepMethods: [] as string[],
    kitchenNotes: [] as string[],
    experienceTypes: [] as string[]
  });

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setIsLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        
        if (profile) {
          setFormData({
            ...formData,
            businessName: profile.first_name + ' ' + profile.last_name,
            businessDescription: profile.cooking_specialties || "",
            taxId: "",
            firstName: profile.first_name || "",
            lastName: profile.last_name || "",
            address: profile.address || "",
            city: profile.city || "",
            state: profile.state || "",
            postalCode: profile.postal_code || "",
            cookingSpecialties: profile.cooking_specialties || "",
            instagramHandle: profile.instagram_handle || "",
            tiktokHandle: profile.tiktok_handle || "",
            facebookHandle: profile.facebook_handle || "",
            pinterestHandle: profile.pinterest_handle || "",
            yearsExperience: profile.years_experience || "",
            otherExperience: profile.other_experience || "",
            prepMethods: profile.prep_methods || [],
            kitchenNotes: profile.kitchen_notes || [],
            experienceTypes: profile.experience_types || []
          });
        }
      } catch (error) {
        console.error("Error fetching profile data:", error);
        toast({
          title: "Error",
          description: "Failed to load profile data",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const profileData = {
        id: user.id,
        first_name: formData.firstName,
        last_name: formData.lastName,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postalCode,
        cooking_specialties: formData.cookingSpecialties,
        instagram_handle: formData.instagramHandle,
        tiktok_handle: formData.tiktokHandle,
        facebook_handle: formData.facebookHandle,
        pinterest_handle: formData.pinterestHandle,
        years_experience: formData.yearsExperience,
        other_experience: formData.otherExperience,
        prep_methods: formData.prepMethods,
        kitchen_notes: formData.kitchenNotes,
        experience_types: formData.experienceTypes,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('profiles')
        .upsert(profileData);

      if (error) throw error;

      toast({
        title: "Settings saved",
        description: "Your business profile has been updated."
      });
    } catch (error: any) {
      console.error("Error saving profile:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save profile",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleCheckboxItem = (field: 'prepMethods' | 'kitchenNotes' | 'experienceTypes', item: string) => {
    setFormData(prev => {
      const currentItems = [...prev[field]];
      const itemIndex = currentItems.indexOf(item);
      
      if (itemIndex > -1) {
        currentItems.splice(itemIndex, 1);
      } else {
        currentItems.push(item);
      }
      
      return { ...prev, [field]: currentItems };
    });
  };

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <h3 className="text-lg font-medium">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Your first name"
                  className="bg-[#EDEDED] border-gray-200 text-gray-900 placeholder:text-[#6B6B6B]"
                />
              </div>
              
              <div>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Your last name"
                  className="bg-[#EDEDED] border-gray-200 text-gray-900 placeholder:text-[#6B6B6B]"
                />
              </div>
            </div>

            <h3 className="text-lg font-medium">Address</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="address">Street Address</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Your street address"
                  className="bg-[#EDEDED] border-gray-200 text-gray-900 placeholder:text-[#6B6B6B]"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Your city"
                    className="bg-[#EDEDED] border-gray-200 text-gray-900 placeholder:text-[#6B6B6B]"
                  />
                </div>
                
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="Your state"
                    className="bg-[#EDEDED] border-gray-200 text-gray-900 placeholder:text-[#6B6B6B]"
                  />
                </div>
                
                <div>
                  <Label htmlFor="postalCode">Postal Code</Label>
                  <Input
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    placeholder="Your postal code"
                    className="bg-[#EDEDED] border-gray-200 text-gray-900 placeholder:text-[#6B6B6B]"
                  />
                </div>
              </div>
            </div>

            <h3 className="text-lg font-medium">Cooking Experience</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="yearsExperience">Years of Cooking Experience</Label>
                <Input
                  id="yearsExperience"
                  name="yearsExperience"
                  value={formData.yearsExperience}
                  onChange={handleChange}
                  placeholder="How many years of cooking experience"
                  className="bg-[#EDEDED] border-gray-200 text-gray-900 placeholder:text-[#6B6B6B]"
                />
              </div>
              
              <div>
                <Label className="block mb-2">Food Prep Methods</Label>
                <div className="grid grid-cols-2 gap-2">
                  {["Smoked", "Oven-baked", "Air-fried", "Roasted", "Slow cooked", "Wood-fired", "Baked goods"].map((method) => (
                    <div key={method} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`prep-${method}`} 
                        checked={formData.prepMethods.includes(method)}
                        onCheckedChange={() => toggleCheckboxItem('prepMethods', method)}
                      />
                      <Label htmlFor={`prep-${method}`} className="text-sm text-gray-900">{method}</Label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="block mb-2">Kitchen Notes</Label>
                <div className="grid grid-cols-2 gap-2">
                  {["Nut free", "Gluten free", "Dairy free", "Pets in house"].map((note) => (
                    <div key={note} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`note-${note}`} 
                        checked={formData.kitchenNotes.includes(note)}
                        onCheckedChange={() => toggleCheckboxItem('kitchenNotes', note)}
                      />
                      <Label htmlFor={`note-${note}`} className="text-sm text-gray-900">{note}</Label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="block mb-2">Types of Cooking Experience</Label>
                <div className="grid grid-cols-2 gap-2">
                  {["Home chef only", "Culinary school", "Restaurant experience", "Cottage kitchen", "Other"].map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`exp-${type}`} 
                        checked={formData.experienceTypes.includes(type)}
                        onCheckedChange={() => toggleCheckboxItem('experienceTypes', type)}
                      />
                      <Label htmlFor={`exp-${type}`} className="text-sm text-gray-900">{type}</Label>
                    </div>
                  ))}
                </div>
              </div>
              
              {formData.experienceTypes.includes("Other") && (
                <div>
                  <Label htmlFor="otherExperience">Other Experience Details</Label>
                  <Input
                    id="otherExperience"
                    name="otherExperience"
                    value={formData.otherExperience}
                    onChange={handleChange}
                    placeholder="Please describe your other cooking experience"
                    className="bg-[#EDEDED] border-gray-200 text-gray-900 placeholder:text-[#6B6B6B]"
                  />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="cookingSpecialties">Cooking Specialties</Label>
              <Textarea
                id="cookingSpecialties"
                name="cookingSpecialties"
                value={formData.cookingSpecialties}
                onChange={handleChange}
                placeholder="Tell us about your cooking expertise and specialties..."
                className="h-32 bg-[#EDEDED] border-gray-200 text-gray-900 placeholder:text-[#6B6B6B]"
              />
            </div>
            
            <h3 className="text-lg font-medium">Social Media Handles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="instagramHandle">Instagram</Label>
                <Input
                  id="instagramHandle"
                  name="instagramHandle"
                  value={formData.instagramHandle}
                  onChange={handleChange}
                  placeholder="@username"
                  className="bg-[#EDEDED] border-gray-200 text-gray-900 placeholder:text-[#6B6B6B]"
                />
              </div>
              
              <div>
                <Label htmlFor="tiktokHandle">TikTok</Label>
                <Input
                  id="tiktokHandle"
                  name="tiktokHandle"
                  value={formData.tiktokHandle}
                  onChange={handleChange}
                  placeholder="@username"
                  className="bg-[#EDEDED] border-gray-200 text-gray-900 placeholder:text-[#6B6B6B]"
                />
              </div>
              
              <div>
                <Label htmlFor="facebookHandle">Facebook</Label>
                <Input
                  id="facebookHandle"
                  name="facebookHandle"
                  value={formData.facebookHandle}
                  onChange={handleChange}
                  placeholder="Username or profile URL"
                  className="bg-[#EDEDED] border-gray-200 text-gray-900 placeholder:text-[#6B6B6B]"
                />
              </div>
              
              <div>
                <Label htmlFor="pinterestHandle">Pinterest</Label>
                <Input
                  id="pinterestHandle"
                  name="pinterestHandle"
                  value={formData.pinterestHandle}
                  onChange={handleChange}
                  placeholder="@username"
                  className="bg-[#EDEDED] border-gray-200 text-gray-900 placeholder:text-[#6B6B6B]"
                />
              </div>
            </div>
            
            <Button type="submit" className="mt-2" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save Profile"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="space-y-4">
          <Shimmer className="h-6 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Shimmer className="h-10 w-full" />
            <Shimmer className="h-10 w-full" />
          </div>
          <Shimmer className="h-6 w-48" />
          <Shimmer className="h-10 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Shimmer className="h-10 w-full" />
            <Shimmer className="h-10 w-full" />
            <Shimmer className="h-10 w-full" />
          </div>
          <Shimmer className="h-6 w-48" />
          <Shimmer className="h-10 w-full" />
          <Shimmer className="h-32 w-full" />
          <Shimmer className="h-6 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Shimmer className="h-10 w-full" />
            <Shimmer className="h-10 w-full" />
            <Shimmer className="h-10 w-full" />
            <Shimmer className="h-10 w-full" />
          </div>
          <Shimmer className="h-10 w-32" />
        </div>
      </Card>
    </div>
  );
}
